import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import { Brain, Clock, Users, ArrowRight } from 'lucide-react';

const MOCK_CASES = [
  {
    id: 'case-1',
    type: 'Canine',
    time: '~15 min',
    title: 'Chronic vomiting in 7-year-old Labrador',
    symptoms: ['Intermittent vomiting (3-4 times per week)', 'Weight loss (2 kg over 3 weeks)', 'Variable appetite'],
    extraSymptoms: 2,
    attempts: 247
  },
  {
    id: 'case-2',
    type: 'Feline',
    time: '~15 min',
    title: 'Dyspneic cat with pleural effusion',
    symptoms: ['Severe dyspnea with open mouth breathing', 'Tachypnea', 'Abdominal effort during breathing'],
    extraSymptoms: 2,
    attempts: 247
  }
];

const CasesPage = () => {
  const navigate = useNavigate();

  return (
    <AppShell>
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto py-6 px-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className="text-gray-500 hover:text-gray-900 flex items-center text-sm font-medium mb-6 transition-colors"
          >
            <span className="mr-1">{"<"}</span> Back to Home
          </button>

          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center shrink-0 shadow-sm">
              <Brain className="text-[#7367f0]" size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Case Challenges</h1>
              <p className="text-gray-500 text-sm mt-1">Test your clinical thinking with real scenarios</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50/50 min-h-screen">
        <div className="max-w-4xl mx-auto py-8 px-4 pb-20">
          <div className="bg-[#f8f5ff] rounded-xl border border-purple-100 p-5 mb-8 shadow-sm">
            <p className="text-gray-700 text-sm">
              <span className="font-semibold text-purple-900">How it works:</span> Review case details, submit your diagnostic approach, then see how a veterinarian handled the same case. Compare your clinical reasoning.
            </p>
          </div>

        <div className="space-y-6">
          {MOCK_CASES.map((c) => (
            <div 
              key={c.id}
              onClick={() => navigate(`/cases/${c.id}/intake`)}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-[#6b46c1]/30 transition-all cursor-pointer group"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex gap-2">
                  <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold border border-blue-100">
                    {c.type}
                  </span>
                  <span className="px-3 py-1 bg-gray-50 text-gray-600 rounded-full text-xs font-medium border border-gray-200 flex items-center gap-1">
                    <Clock size={12} /> {c.time}
                  </span>
                </div>
                <ArrowRight className="text-gray-300 group-hover:text-[#6b46c1] transition-colors" size={20} />
              </div>

              <h2 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#6b46c1] transition-colors">
                {c.title}
              </h2>

              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-800 mb-2">Key symptoms to consider:</p>
                <div className="flex flex-wrap gap-2">
                  {c.symptoms.map((s, idx) => (
                    <span key={idx} className="px-3 py-1.5 bg-white text-gray-700 rounded-md text-xs border border-gray-200 shadow-sm">
                      {s}
                    </span>
                  ))}
                  <span className="px-3 py-1.5 bg-white text-gray-500 rounded-md text-xs border border-gray-200 shadow-sm font-medium">
                    +{c.extraSymptoms} more
                  </span>
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                <div className="flex items-center gap-1.5 text-gray-400 text-sm font-medium">
                  <Users size={16} />
                  {c.attempts} attempts
                </div>
                <div className="text-[#7367f0] font-semibold text-sm flex items-center group-hover:underline">
                  Start Challenge <ArrowRight size={16} className="ml-1" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </AppShell>
  );
};

export default CasesPage;
